import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, Check, Loader2, Send, AlertTriangle, Shield } from 'lucide-react';

interface PaymentStepProps {
  amount: number;
  credits: number;
  walletAddress: string;
  isSubmitting: boolean;
  onBack: () => void;
  onConfirmPayment: () => void;
}

export function PaymentStep({
  amount,
  credits,
  walletAddress,
  isSubmitting,
  onBack,
  onConfirmPayment,
}: PaymentStepProps) {
  const [copied, setCopied] = useState(false);

  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(walletAddress)}`;

  const handleCopyAddress = async () => {
    await navigator.clipboard.writeText(walletAddress);
    setCopied(true);
    toast.success('Wallet address copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-5">
      {/* Amount Summary */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="py-4 text-center space-y-1">
          <p className="text-sm text-muted-foreground">Send exactly</p>
          <p className="text-3xl font-bold text-primary">{amount} USDT</p>
          <Badge variant="secondary" className="mt-2">
            Network: TRON (TRC20)
          </Badge>
          <p className="text-xs text-muted-foreground mt-2">
            = {credits.toLocaleString()} credits
          </p>
        </CardContent>
      </Card>

      {/* QR Code and Address */}
      <div className="space-y-4">
        <Label className="text-sm font-medium">Send to this address:</Label>
        
        <div className="flex justify-center">
          <div className="bg-white p-4 rounded-xl shadow-sm border">
            <img 
              src={qrCodeUrl} 
              alt="Deposit wallet QR code" 
              className="w-36 h-36"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <Input
            value={walletAddress}
            readOnly
            className="font-mono text-xs bg-muted/50"
          />
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleCopyAddress}
            className="shrink-0"
          >
            {copied ? (
              <Check className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>

      {/* Warning */}
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="space-y-2">
            <p className="text-sm font-medium text-amber-700 dark:text-amber-400">
              Important — Read Before Sending
            </p>
            <ul className="text-xs text-amber-600 dark:text-amber-300 space-y-1.5">
              <li>• Send <strong>ONLY USDT</strong> on the <strong>TRON (TRC20)</strong> network</li>
              <li>• Sending on any other network will result in <strong>loss of funds</strong></li>
              <li>• Do not send from exchanges that don't support TRC20 withdrawals</li>
              <li>• Minimum deposit: {amount} USDT</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Trust Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Shield className="h-3.5 w-3.5 text-primary" />
        <span>Verified via TronGrid blockchain monitoring</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={onBack}
          disabled={isSubmitting}
        >
          Back
        </Button>
        <Button
          className="flex-1"
          onClick={onConfirmPayment}
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              I Have Sent the Payment
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
